import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, Mail, Handshake, RefreshCw, Inbox } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { IMAGES } from "../lib/data";

function StatCard({ icon: Icon, label, value, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-[#101012] border border-zinc-800 p-6 flex items-center gap-5"
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="h-14 w-14 flex items-center justify-center bg-black border border-zinc-800 text-orange-500">
        <Icon size={26} />
      </div>
      <div>
        <p className="font-display text-4xl font-black text-white tabular-nums">{value}</p>
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ enquiries: 0, subscribers: 0, dealer_leads: 0 });
  const [enquiries, setEnquiries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, n] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/enquiries"),
        api.get("/admin/newsletter"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setSubs(n.data);
    } catch {
      /* handled by interceptor / auth guard */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); navigate("/admin/login"); };
  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="border-b border-white/5 bg-[#0a0a0a] sticky top-0 z-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="Sobtrex" className="h-9 w-auto" />
            <span className="hidden sm:inline text-xs font-bold uppercase tracking-widest text-zinc-500">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="h-10 w-10 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:text-orange-500 hover:border-orange-500 transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <span className="hidden sm:inline text-sm text-zinc-500">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-orange-500 hover:border-orange-500 px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 md:px-8 py-10">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase text-white">Enquiries Overview</h1>

        <div className="mt-8 grid sm:grid-cols-3 gap-5">
          <StatCard icon={Users} label="Total Enquiries" value={stats.enquiries} delay={0.05} />
          <StatCard icon={Handshake} label="Dealer Leads" value={stats.dealer_leads} delay={0.1} />
          <StatCard icon={Mail} label="Subscribers" value={stats.subscribers} delay={0.15} />
        </div>

        <Tabs defaultValue="enquiries" className="mt-10">
          <TabsList className="bg-[#101012] border border-zinc-800">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black uppercase text-xs font-bold">Enquiries</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black uppercase text-xs font-bold">Subscribers</TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <Empty label="No enquiries yet." />
            ) : (
              <div className="overflow-x-auto border border-zinc-800">
                <table className="w-full text-left text-sm" data-testid="enquiries-table">
                  <thead className="bg-[#101012] text-zinc-500 uppercase text-xs tracking-wider">
                    <tr>
                      {["Name", "Company", "Type", "Email", "Phone", "Location", "Message", "Date"].map((h) => (
                        <th key={h} className="px-4 py-3 font-bold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((e) => (
                      <tr key={e.id} className="border-t border-zinc-800 hover:bg-white/[0.02]" data-testid={`enquiry-row-${e.id}`}>
                        <td className="px-4 py-3 text-white font-semibold whitespace-nowrap">{e.full_name}</td>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{e.company_name || "—"}</td>
                        <td className="px-4 py-3"><span className="text-orange-500 font-semibold">{e.business_type}</span></td>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{e.email}</td>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{e.phone}</td>
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{[e.city, e.state, e.country].filter(Boolean).join(", ") || "—"}</td>
                        <td className="px-4 py-3 text-zinc-500 max-w-[220px] truncate" title={e.message}>{e.message || "—"}</td>
                        <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{fmt(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="subscribers" className="mt-6">
            {subs.length === 0 ? (
              <Empty label="No subscribers yet." />
            ) : (
              <div className="overflow-x-auto border border-zinc-800">
                <table className="w-full text-left text-sm" data-testid="subscribers-table">
                  <thead className="bg-[#101012] text-zinc-500 uppercase text-xs tracking-wider">
                    <tr><th className="px-4 py-3 font-bold">Email</th><th className="px-4 py-3 font-bold">Subscribed</th></tr>
                  </thead>
                  <tbody>
                    {subs.map((s) => (
                      <tr key={s.id} className="border-t border-zinc-800 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white">{s.email}</td>
                        <td className="px-4 py-3 text-zinc-600">{fmt(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="border border-dashed border-zinc-800 py-20 text-center text-zinc-600" data-testid="empty-state">
      <Inbox size={40} className="mx-auto mb-4 opacity-50" />
      {label}
    </div>
  );
}
